import { useAuth } from '@context/auth/base';
import { ChannelPageSocialProofDocument, gql, useInfiniteQuery } from '@graphql/reactQuery';

/*#__PURE__*/ gql(/* GraphQL */ `
  query ChannelPageSocialProof($channelId: UUID!, $pagination: CursorConnectionArgs!) {
    channelMembersWhoAreFollowed(input: { channelId: $channelId }) {
      __typename
      ... on QueryChannelMembersWhoAreFollowedSuccess {
        data {
          count
          members(pagination: $pagination) {
            edges {
              node {
                id
                user {
                  id
                  ...GroupedUsersInfo
                  ...UserModalRow
                }
              }
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      }
    }
  }
`);

export function useChannelFollowedMembers({
  channelId,
  limit,
}: {
  channelId: string;
  limit: number;
}) {
  const { authUser } = useAuth();

  const {
    orderedList: members,
    loadMoreNextPage,
    isLoading,
  } = useInfiniteQuery(ChannelPageSocialProofDocument, {
    enabled: !!authUser,
    filterQueryKey: {
      channelId,
      limit,
    },
    getNextPageParam({ data: { channelMembersWhoAreFollowed } }) {
      if (channelMembersWhoAreFollowed.__typename === 'QueryChannelMembersWhoAreFollowedSuccess') {
        return (
          channelMembersWhoAreFollowed?.data.members.pageInfo.hasNextPage && {
            after: channelMembersWhoAreFollowed?.data.members.pageInfo.endCursor,
          }
        );
      }

      return;
    },
    variables({ pageParam }) {
      return {
        channelId,
        pagination: {
          first: limit,
          after: pageParam?.after,
        },
      };
    },
    list({ channelMembersWhoAreFollowed }) {
      if (channelMembersWhoAreFollowed.__typename === 'QueryChannelMembersWhoAreFollowedSuccess') {
        return channelMembersWhoAreFollowed.data.members.edges.map(edge => edge.node.user);
      }

      return [];
    },
    uniq({ id }) {
      return id;
    },
    staleTime: 0,
  });

  const memberCount = members.length;

  return {
    members,
    memberCount,
    isLoading,
    loadMore: loadMoreNextPage,
  };
}
